// Branch scope selector for controlling data query scope across all analysis views

import * as $store from '@/store'

/**
 * Initialize the branch scope selector in the main filters area
 * Allows users to choose between querying current branch or all branches
 */
export function initBranchScopeSelector(): void {
  const mainFilters = document.getElementById('main-filters')
  if (!mainFilters) {
    console.warn('main-filters element not found, cannot initialize branch scope selector')
    return
  }

  // Check if selector already exists
  if (document.getElementById('branch-scope-selector-container')) {
    return
  }

  // Get current scope from store
  const currentScope = $store.getQueryScope()
  const userBranches = $store.getUserBranches()

  // Only show if user has multiple branches
  const showSelector = userBranches.length > 1

  // Create the selector HTML
  const selectorHTML = `
    <div id="branch-scope-selector-container" class="mb-4 ${!showSelector ? 'hidden' : ''}">
      <div class="flex items-center gap-4 bg-blue-50 p-3 rounded-md border border-blue-200">
        <label for="branch-scope-select" class="text-sm font-medium text-gray-700 whitespace-nowrap">
          Data Scope:
        </label>
        <select id="branch-scope-select" class="flex-1 p-2 border rounded-md bg-white text-sm">
          <option value="current" ${currentScope === 'current' ? 'selected' : ''}>
            Current Branch Only
          </option>
          <option value="all" ${currentScope === 'all' ? 'selected' : ''}>
            All My Branches (${userBranches.length} branches)
          </option>
        </select>
        <span class="text-xs text-gray-500">
          <svg class="inline w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span id="branch-scope-info">
            ${currentScope === 'current' ? 'Showing data from selected branch only' : 'Showing aggregated data from all your branches'}
          </span>
        </span>
      </div>
    </div>
  `

  // Insert at the beginning of main-filters
  const filtersInnerDiv = mainFilters.querySelector('.grid') || mainFilters
  mainFilters.insertAdjacentHTML('afterbegin', selectorHTML)

  // Attach event listener
  const scopeSelect = document.getElementById('branch-scope-select') as HTMLSelectElement
  const scopeInfo = document.getElementById('branch-scope-info') as HTMLSpanElement

  if (scopeSelect) {
    scopeSelect.addEventListener('change', () => {
      const newScope = scopeSelect.value as 'current' | 'all'

      // Update store
      $store.setQueryScope(newScope)

      // Update info text
      if (scopeInfo) {
        scopeInfo.textContent = newScope === 'current'
          ? 'Showing data from selected branch only'
          : 'Showing aggregated data from all your branches'
      }

      // Clear cached view data to force refresh
      $store.clearAllViewData()

      // Show notification
      const notification = document.createElement('div')
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300'
      notification.textContent = `Data scope changed to: ${newScope === 'current' ? 'Current Branch' : 'All Branches'}. Please re-apply filters to load data.`
      document.body.appendChild(notification)

      setTimeout(() => {
        notification.style.opacity = '0'
        setTimeout(() => notification.remove(), 300)
      }, 3000)
    })
  }
}

/**
 * Update the branch scope selector visibility based on user's branch count
 */
export function updateBranchScopeSelectorVisibility(): void {
  const container = document.getElementById('branch-scope-selector-container')
  if (!container) return

  const userBranches = $store.getUserBranches()
  const showSelector = userBranches.length > 1

  if (showSelector) {
    container.classList.remove('hidden')

    // Update the "all branches" option text
    const allOption = container.querySelector('option[value="all"]') as HTMLOptionElement
    if (allOption) {
      allOption.textContent = `All My Branches (${userBranches.length} branches)`
    }
  } else {
    container.classList.add('hidden')
    // Automatically set to 'current' if user has only 1 branch
    $store.setQueryScope('current')
  }
}

/**
 * Get the current query scope setting
 */
export function getQueryScope(): 'current' | 'all' {
  return $store.getQueryScope()
}

/**
 * Check if the current query scope is set to 'all branches'
 */
export function isQueryingAllBranches(): boolean {
  return $store.getQueryScope() === 'all'
}

/**
 * Check if the current query scope is set to 'current branch'
 */
export function isQueryingCurrentBranch(): boolean {
  return $store.getQueryScope() === 'current'
}
